import classes from "./Landing.module.scss";
import { useRef, useEffect, useState, useContext, useMemo } from "react";
import ValidUserContext from "../authCheck";

const isMobileDevice = () => /Mobi|Android/i.test(navigator.userAgent);

const workbookKeyFromViewUrl = (viewUrl) => {
  if (!viewUrl) return "";
  return viewUrl.split("/sheets/")[0] || viewUrl.split("/")[0] || viewUrl;
};

const buildVizUrl = (viewPath, reloadKey) => {
  const base =
    JSON.parse(localStorage.getItem("dashboard-url")) ||
    "https://tableau.conferencedirect.com/#/site/conferencedirect/views/";
  // Keep workbook + sheet in the initial src; sheet switches use activateSheetAsync
  const reload = reloadKey != null ? `&_r=${reloadKey}` : "";
  return (
    base +
    viewPath.replace("/sheets", "") +
    "?:showVizHome=no&:embed=true&:toolbar=no&:tabs=n" +
    reload
  );
};

const findSheetForViewUrl = (sheets, viewUrl) => {
  if (!sheets?.length || !viewUrl) return null;

  const sheetSeg = viewUrl.split("/sheets/")[1] || viewUrl.split("/").pop() || "";
  const compactSeg = sheetSeg.replace(/[\s_-]+/g, "").toLowerCase();
  const pathWithoutSheets = viewUrl.replace("/sheets/", "/");

  return (
    sheets.find((s) => s.url && (s.url.includes(viewUrl) || s.url.includes(pathWithoutSheets))) ||
    sheets.find((s) => s.name === sheetSeg) ||
    sheets.find(
      (s) => (s.name || "").replace(/[\s_-]+/g, "").toLowerCase() === compactSeg
    ) ||
    null
  );
};

const activateSheetByViewUrl = async (viz, viewUrl) => {
  if (!viz?.workbook || !viewUrl) return false;
  const sheets = viz.workbook.publishedSheetsInfo || [];
  const sheet = findSheetForViewUrl(sheets, viewUrl);
  if (!sheet) {
    console.warn("Sheet not found for view:", viewUrl, sheets.map((s) => s.name));
    return false;
  }
  try {
    await viz.workbook.activateSheetAsync(sheet.name);
    return true;
  } catch (err) {
    console.error("activateSheetAsync failed", err);
    return false;
  }
};

const HEARTBEAT_MS = 15 * 60 * 1000;
const LOAD_TIMEOUT_MS = 30000;

const Dashboard = ({
  embedUrl,
  activeSheetUrl,
  displayTabs = false,
  isVisible = true,
  onSessionExpired,
  onVizReady,
}) => {
  const elementRef = useRef(null);
  const readyRef = useRef(false);
  const activeSheetRef = useRef(activeSheetUrl);
  const displayedViewUrlRef = useRef(activeSheetUrl || embedUrl);
  const workbookKeyRef = useRef(workbookKeyFromViewUrl(embedUrl));
  const onSessionExpiredRef = useRef(onSessionExpired);
  const onVizReadyRef = useRef(onVizReady);
  const validUserContext = useContext(ValidUserContext);

  onSessionExpiredRef.current = onSessionExpired;
  onVizReadyRef.current = onVizReady;

  const [src, setSrc] = useState(() => {
    const initial = activeSheetUrl || embedUrl;
    return initial ? buildVizUrl(initial) : "";
  });
  const srcRef = useRef(src);
  const [token] = useState(() => {
    const jwtToken = JSON.parse(localStorage.getItem("tableau-login-data"));
    if (jwtToken && jwtToken !== "redeemed") {
      localStorage.setItem("tableau-login-data", JSON.stringify("redeemed"));
      return jwtToken;
    }
    return null;
  });

  const [tabArray, setTabArray] = useState([]);
  const [tabLinksArray, setTabLinksArray] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isVizLoading, setIsVizLoading] = useState(() => Boolean(src));

  activeSheetRef.current = activeSheetUrl;

  const notifySessionExpired = (reason) => {
    console.warn("Tableau session recovery:", reason || "unknown");
    if (onSessionExpiredRef.current) {
      onSessionExpiredRef.current(reason);
      return;
    }
    validUserContext.logoutUser(reason);
  };

  const loadViewByUrl = (viewUrl) => {
    if (!viewUrl) return;
    displayedViewUrlRef.current = viewUrl;
    const canonicalSrc = buildVizUrl(viewUrl);
    // Same src as the current attribute does not reload the Tableau web component,
    // which breaks Back after activateSheetAsync left a different sheet on screen.
    const nextSrc =
      srcRef.current.split("&_r=")[0] === canonicalSrc
        ? buildVizUrl(viewUrl, Date.now())
        : canonicalSrc;
    readyRef.current = false;
    setIsVizLoading(true);
    localStorage.setItem("tableauActive", false);
    srcRef.current = nextSrc;
    setSrc(nextSrc);
  };

  // Workbook change → reload viz src. View change within workbook → activateSheetAsync.
  useEffect(() => {
    if (!embedUrl) return;
    const nextKey = workbookKeyFromViewUrl(embedUrl);
    if (nextKey !== workbookKeyRef.current) {
      workbookKeyRef.current = nextKey;
      loadViewByUrl(activeSheetRef.current || embedUrl);
    }
  }, [embedUrl]);

  useEffect(() => {
    const viz = elementRef.current;
    if (!viz) return undefined;

    const onFirstInteractive = async () => {
      readyRef.current = true;
      localStorage.setItem("tableauActive", true);
      onVizReadyRef.current?.();

      const sheets = viz.workbook?.publishedSheetsInfo || [];
      const names = sheets.map((sheet) =>
        isMobileDevice() ? sheet.index + 1 : sheet.name
      );
      setTabArray(names);
      setTabLinksArray(sheets.map((sheet) => sheet.name));

      const targetUrl = activeSheetRef.current || embedUrl;
      const idx = sheets.findIndex((sheet) => {
        const match = findSheetForViewUrl([sheet], targetUrl);
        return !!match;
      });
      if (idx >= 0) setActiveTab(idx);

      if (!targetUrl) {
        displayedViewUrlRef.current = targetUrl;
        setIsVizLoading(false);
        return;
      }

      const activated = await activateSheetByViewUrl(viz, targetUrl);
      if (activated) {
        displayedViewUrlRef.current = targetUrl;
        if (activeSheetRef.current === targetUrl) {
          setIsVizLoading(false);
        }
        return;
      }

      displayedViewUrlRef.current = targetUrl;
      const loadedThisView =
        srcRef.current.split("&_r=")[0] === buildVizUrl(targetUrl);
      if (!loadedThisView) {
        loadViewByUrl(targetUrl);
        return;
      }
      if (activeSheetRef.current === targetUrl) {
        setIsVizLoading(false);
      }
    };

    const onVizLoadError = () => {
      readyRef.current = false;
      setIsVizLoading(true);
      notifySessionExpired("vizloaderror");
    };

    viz.addEventListener("firstinteractive", onFirstInteractive);
    viz.addEventListener("vizloaderror", onVizLoadError);

    return () => {
      viz.removeEventListener("firstinteractive", onFirstInteractive);
      viz.removeEventListener("vizloaderror", onVizLoadError);
    };
    // Mount once per Dashboard instance; workbook reloads happen via src change on same element
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slow Tableau loads are normal; do not treat them as a dead session.
  useEffect(() => {
    if (!src) return undefined;
    const timeoutId = setTimeout(() => {
      if (!readyRef.current) {
        console.warn(
          `Tableau viz still loading after ${LOAD_TIMEOUT_MS}ms; keeping overlay visible`
        );
      }
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [src]);

  // Keep the Tableau iframe session alive while the viz stays mounted (including admin hide).
  useEffect(() => {
    const viz = elementRef.current;
    if (!viz) return undefined;
    const heartbeatId = setInterval(async () => {
      if (!readyRef.current) return;
      try {
        await viz.refreshDataAsync();
      } catch (err) {
        notifySessionExpired("heartbeat_refresh_failed");
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(heartbeatId);
  }, []);

  // Same-workbook view navigation — preserve filters via activateSheetAsync.
  // REST views are not always in publishedSheetsInfo; load that view URL instead.
  // isVisible re-applies the view when Back returns from admin (display:none blanks the iframe).
  useEffect(() => {
    if (!isVisible || !readyRef.current || !activeSheetUrl) return;
    const viz = elementRef.current;
    if (!viz?.workbook) return;

    const targetUrl = activeSheetUrl;
    setIsVizLoading(true);
    activateSheetByViewUrl(viz, targetUrl).then((activated) => {
      if (activeSheetRef.current !== targetUrl) return;
      if (activated) {
        displayedViewUrlRef.current = targetUrl;
        const sheets = viz.workbook.publishedSheetsInfo || [];
        const idx = sheets.findIndex(
          (sheet) => !!findSheetForViewUrl([sheet], targetUrl)
        );
        if (idx >= 0) setActiveTab(idx);
        setIsVizLoading(false);
        return;
      }
      loadViewByUrl(targetUrl);
    });
  }, [activeSheetUrl, isVisible]);

  const handleTabClick = (tabIndex) => {
    validUserContext.localAuthCheck(false);
    const name = tabLinksArray[tabIndex];
    const viz = elementRef.current;
    setActiveTab(tabIndex);
    if (!viz?.workbook || !name) return;
    setIsVizLoading(true);
    viz.workbook.activateSheetAsync(name).finally(() => {
      setIsVizLoading(false);
    });
  };

  const inputProps = useMemo(() => (token ? { token } : {}), [token]);

  if (!src) {
    return <div className={classes.vizWrapper} />;
  }

  return (
    <div className={classes.vizWrapper}>
      {displayTabs ? (
        <div className={classes.tabbar}>
          <span className={classes.tabs}>
            {tabArray.map((tab, index) => (
              <span
                key={index}
                className={`${classes.tab} ${activeTab === index ? classes.active : ""}`}
                onClick={() => handleTabClick(index)}
              >
                {tab}
              </span>
            ))}
          </span>
        </div>
      ) : (
        <div />
      )}
      <tableau-viz
        class={classes.tabframe}
        ref={elementRef}
        id="tableauViz"
        width="100%"
        height="100%"
        hide-tabs="true"
        toolbar="hidden"
        src={src}
        {...inputProps}
      />
      {isVizLoading && (
        <div className={classes.vizLoadingOverlay} role="status" aria-live="polite">
          <div className={classes.vizLoadingMessage}>
            Loading
            <span className={classes.vizLoadingDots} aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
